import type Phaser from "phaser";
import { CHARACTER_BY_ID, CONTENT } from "../shared/content";
import { formatDifficultyTier, normalizeDifficultyTier } from "../shared/difficulty";
import { DISPLAY_NAME_TAKEN_MESSAGE, lobbyLabel } from "../shared/lobby";
import { playerColorForSlot } from "../shared/playerNameColor";
import {
  createRoomCode,
  DISPLAY_NAME_MAX,
  normalizeDisplayName,
  normalizeRoomCode
} from "../shared/protocol";
import {
  DIFFICULTY_TIERS,
  type CharacterDefinition,
  type DifficultyTier,
  type LobbyState,
  type SpriteAnimationDefinition,
  type WorldState
} from "../shared/types";
import type { GameOptions } from "./GameScene";
import { bindAudioOptionsMenu, closeOptionsMenu, isOptionsMenuOpen } from "./audioOptions";
import { bindWinamp, isSoundtrackOpen } from "./winamp";
import { bindMenuGamepad } from "./menuNav";
import { buildPlayerSlots, slotGrid, STAT_PIPS, statPips } from "./playerSlots";
import { registerLiveGame, teardownLiveAudio } from "./audioLifetime";
import { getControlSettings, subscribeControlSettings } from "./controlSettings";
import { stopGameBgm } from "./gameBgm";
import { createMenuBgm } from "./menuBgm";
import { RoomConnection } from "./network";
import { loadPlayerName, savePlayerName } from "./playerName";
import { bindControlHints } from "./inputDevice";
import { bindTouchOverlay } from "./touchControls";
import { clearPersistedRoomCode, persistRoomCode, readPersistedRoomCode } from "./roomSession";
import "./styles.css";

teardownLiveAudio();

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`The game shell is missing ${selector}.`);
  return element;
}

const menu = requiredElement<HTMLElement>("#menu");
const gameShell = requiredElement<HTMLElement>("#game-shell");
const gameHost = requiredElement<HTMLElement>("#game");
const characterSelect = requiredElement<HTMLElement>(".character-select");
const characterGrid = requiredElement<HTMLElement>("#characters");
const fighterSpotlight = requiredElement<HTMLElement>("#fighter-spotlight");
const rosterCount = requiredElement<HTMLElement>("#roster-count");
const selectedFighterName = requiredElement<HTMLElement>("#selected-fighter-name");
const selectedFighterSpecial = requiredElement<HTMLElement>("#selected-fighter-special");
const selectedFighterVisual = requiredElement<HTMLElement>("#selected-fighter-visual");
const practiceButton = requiredElement<HTMLButtonElement>("#practice");
const practiceStart = requiredElement<HTMLSelectElement>("#practice-start");
const createButton = requiredElement<HTMLButtonElement>("#create-room");
const joinForm = requiredElement<HTMLFormElement>("#join-form");
const roomInput = requiredElement<HTMLInputElement>("#room-code");
const status = requiredElement<HTMLElement>("#menu-status");
const exitButton = requiredElement<HTMLButtonElement>("#exit-game");
const copyButton = requiredElement<HTMLButtonElement>("#copy-room");
const fullscreenButton = requiredElement<HTMLButtonElement>("#toggle-fullscreen");
const roomLabel = requiredElement<HTMLElement>("#room-label");
const gameOptionsDetails = requiredElement<HTMLElement>("#game-options-details");
const optionsRoomLabel = requiredElement<HTMLElement>("#options-room-label");
const optionsExitButton = requiredElement<HTMLButtonElement>("#options-exit-game");
const optionsCopyButton = requiredElement<HTMLButtonElement>("#options-copy-room");
const optionsFullscreenButton = requiredElement<HTMLButtonElement>("#options-toggle-fullscreen");
const joinPanel = requiredElement<HTMLElement>("#join-panel");
const joinStepLabel = requiredElement<HTMLElement>("#join-step-label");
const joinHeading = requiredElement<HTMLElement>("#join-heading");
const lobbyHint = requiredElement<HTMLElement>("#lobby-hint");
const readyWait = requiredElement<HTMLElement>("#ready-wait");
const readyWaitKicker = requiredElement<HTMLElement>("#ready-wait-kicker");
const readyWaitDetail = requiredElement<HTMLElement>("#ready-wait-detail");
const playerSlots = requiredElement<HTMLElement>("#player-slots");
const lobbyRoomCode = requiredElement<HTMLElement>("#lobby-room-code");
const readyButton = requiredElement<HTMLButtonElement>("#ready-up");
const copyInviteButton = requiredElement<HTMLButtonElement>("#copy-invite");
const leaveLobbyButton = requiredElement<HTMLButtonElement>("#leave-lobby");
const nameInput = requiredElement<HTMLInputElement>("#player-name");
const nameError = requiredElement<HTMLElement>("#player-name-error");
const difficultyHostNote = requiredElement<HTMLElement>("#difficulty-host-note");
const difficultyButtons = DIFFICULTY_TIERS.map((tier) =>
  requiredElement<HTMLButtonElement>(`#difficulty-${tier}`)
);

const DIFFICULTY_STORAGE_KEY = "breakroom-difficulty";

let selectedCharacter = CONTENT.characters[0]?.id ?? "rook";
let game: Phaser.Game | null = null;
let currentRoom: string | null = null;
let roomConnection: RoomConnection | null = null;
let lobbyPlayerId: string | null = null;
let currentLobby: LobbyState | null = null;
let launchingMatch = false;
let renameTimer = 0;
let reconnectRoomAfterNameChange: string | null = null;
let intentionalRoomLeave = false;
let pushHostDifficulty = false;
let activeConnectionId = 0;
let spotlightAnimationFrame = 0;
let gameModulesPromise: ReturnType<typeof loadGameModules> | null = null;
rosterCount.textContent = `${CONTENT.characters.length} available`;
characterGrid.style.setProperty("--roster-count", String(Math.max(1, CONTENT.characters.length)));
characterGrid.style.setProperty(
  "--roster-half",
  String(Math.max(1, Math.ceil(CONTENT.characters.length / 2)))
);
const STAT_FIELDS = [
  { key: "maxHealth", label: "HP" },
  { key: "walkSpeed", label: "SPD" },
  { key: "jumpSpeed", label: "JMP" }
] as const;
const statRanges = STAT_FIELDS.map(({ key }) => {
  const values = CONTENT.characters.map((character) => character.stats[key]);
  return { min: Math.min(...values), max: Math.max(...values) };
});
let soundtrackOpen = false;
const menuBgm = createMenuBgm(() => !menu.hidden && !soundtrackOpen);
bindWinamp({
  onOpen() {
    soundtrackOpen = true;
    closeOptionsMenu();
    menuBgm.stop();
    menuBgm.sync();
  },
  onClose() {
    soundtrackOpen = false;
    menuBgm.sync();
  }
});
bindAudioOptionsMenu();
bindControlHints();
bindTouchOverlay();
gameShell.classList.toggle("game-chrome-hidden", !getControlSettings().showGameChrome);
subscribeControlSettings((settings) => {
  gameShell.classList.toggle("game-chrome-hidden", !settings.showGameChrome);
  refreshGameScale();
});
menuBgm.sync();

window.addEventListener("pagehide", () => {
  window.cancelAnimationFrame(spotlightAnimationFrame);
  teardownLiveAudio();
});
const hot = (import.meta as ImportMeta & { hot?: { dispose: (callback: () => void) => void } }).hot;
hot?.dispose(() => {
  teardownLiveAudio();
});

CONTENT.levels.forEach((level, levelIndex) => {
  const startOption = document.createElement("option");
  startOption.value = `${levelIndex}:start`;
  startOption.textContent = `${level.name} — beginning`;
  practiceStart.append(startOption);

  const bossWave = level.waves.find((wave) => wave.id === "boss");
  if (!bossWave) return;
  const bossId = bossWave.spawns[0]?.enemyId;
  const bossName = CONTENT.enemies.find((enemy) => enemy.id === bossId)?.displayName ?? "Boss";
  const bossOption = document.createElement("option");
  bossOption.value = `${levelIndex}:boss`;
  bossOption.textContent = `${level.name} — ${bossName}`;
  practiceStart.append(bossOption);

  if (bossId === "dario") {
    const secondFormOption = document.createElement("option");
    secondFormOption.value = `${levelIndex}:boss2`;
    secondFormOption.textContent = `${level.name} — ${bossName} SECOND FORM`;
    practiceStart.append(secondFormOption);
  }
});

for (const character of CONTENT.characters) {
  const button = document.createElement("button");
  button.className = `character-card${character.id === selectedCharacter ? " selected" : ""}`;
  button.type = "button";
  button.dataset.character = character.id;
  button.setAttribute("aria-label", `Select ${character.displayName}`);
  button.setAttribute("aria-pressed", String(character.id === selectedCharacter));
  button.style.setProperty("--fighter-primary", character.palette.primary);
  button.style.setProperty("--fighter-secondary", character.palette.secondary);
  button.style.setProperty("--fighter-accent", character.palette.accent);

  const picks = document.createElement("span");
  picks.className = "fighter-picks";

  const preview = document.createElement("span");
  preview.className = "fighter-portrait";
  if (character.spriteSheet) {
    const sprite = document.createElement("span");
    sprite.className = "fighter-portrait-sprite";
    sprite.dataset.fighter = character.id;
    sprite.style.backgroundImage = `url("${character.spriteSheet.image}")`;
    preview.append(sprite);
  } else {
    preview.append(createPortraitPlaceholder());
  }

  const name = document.createElement("span");
  name.className = "fighter-name";
  name.textContent = character.displayName;
  button.append(picks, preview, name);

  button.addEventListener("click", () => {
    selectFighter(character.id);
  });
  characterGrid.append(button);
}

renderFighterSpotlight(CHARACTER_BY_ID.get(selectedCharacter) ?? CONTENT.characters[0]);
paintFighterPicks();

function createPortraitPlaceholder(): HTMLElement {
  const portrait = document.createElement("span");
  portrait.className = "portrait-placeholder";
  for (const className of [
    "portrait-hair",
    "portrait-head",
    "portrait-neck",
    "portrait-shoulders"
  ]) {
    const part = document.createElement("span");
    part.className = className;
    portrait.append(part);
  }
  return portrait;
}

function createSpotlightPlaceholder(): HTMLElement {
  const puppet = document.createElement("span");
  puppet.className = "spotlight-puppet";
  for (const className of [
    "spotlight-head",
    "spotlight-body",
    "spotlight-leg",
    "spotlight-leg right",
    "spotlight-arm",
    "spotlight-arm right"
  ]) {
    const part = document.createElement("span");
    part.className = className;
    puppet.append(part);
  }
  return puppet;
}

function selectFighter(characterId: string, notifyRoom = true): void {
  const character = CHARACTER_BY_ID.get(characterId);
  if (!character) return;
  selectedCharacter = character.id;
  document.querySelectorAll<HTMLElement>(".character-card").forEach((card) => {
    const isSelected = card.dataset.character === character.id;
    card.classList.toggle("selected", isSelected);
    card.setAttribute("aria-pressed", String(isSelected));
  });
  renderFighterSpotlight(character);
  renderPlayerSlots();
  paintFighterPicks();
  if (notifyRoom) roomConnection?.selectCharacter(character.id);
}

function paintFighterPicks(): void {
  const players = currentLobby?.players ?? [];
  const localColor = players.find((player) => player.id === lobbyPlayerId)?.color;
  const picks =
    players.length > 0
      ? players.map((player) => ({
          characterId: player.id === lobbyPlayerId ? selectedCharacter : player.characterId,
          label: lobbyLabel(player.slot),
          color: player.color
        }))
      : [{ characterId: selectedCharacter, label: lobbyLabel(0), color: localColor ?? playerColorForSlot(0) }];
  document.querySelectorAll<HTMLElement>(".character-card").forEach((card) => {
    const holder = card.querySelector(".fighter-picks");
    if (!holder) return;
    holder.replaceChildren();
    for (const pick of picks.filter((entry) => entry.characterId === card.dataset.character)) {
      const badge = document.createElement("span");
      badge.className = "fighter-pick";
      if (pick.color) badge.style.setProperty("--pick-color", pick.color);
      badge.textContent = pick.label;
      holder.append(badge);
    }
  });
}

function renderFighterSpotlight(character?: CharacterDefinition): void {
  if (!character) return;
  window.cancelAnimationFrame(spotlightAnimationFrame);
  fighterSpotlight.style.setProperty("--fighter-primary", character.palette.primary);
  fighterSpotlight.style.setProperty("--fighter-secondary", character.palette.secondary);
  fighterSpotlight.style.setProperty("--fighter-accent", character.palette.accent);
  selectedFighterName.textContent = character.displayName;
  selectedFighterSpecial.textContent = character.special.label;
  selectedFighterVisual.replaceChildren();

  if (!character.spriteSheet) {
    selectedFighterVisual.append(createSpotlightPlaceholder());
    return;
  }

  const sprite = document.createElement("span");
  sprite.className = "spotlight-sprite";
  sprite.dataset.fighter = character.id;
  sprite.style.backgroundImage = `url("${character.spriteSheet.image}")`;
  selectedFighterVisual.append(sprite);
  animateSpotlightSprite(
    sprite,
    character.spriteSheet.animations.idle,
    character.spriteSheet.animations.attack1
  );
}

function animateSpotlightSprite(
  sprite: HTMLElement,
  idle?: SpriteAnimationDefinition,
  attack?: SpriteAnimationDefinition
): void {
  const idleAnimation = idle ?? { start: 0, end: 0, frameRate: 1 };
  const attackAnimation = attack ?? idleAnimation;
  const idleDuration = 2_400;
  const attackDuration =
    ((attackAnimation.end - attackAnimation.start + 1) / Math.max(1, attackAnimation.frameRate)) *
    1_000;
  const cycleDuration = idleDuration + attackDuration + 600;
  const startedAt = performance.now();

  const paint = (now: number): void => {
    const cycleTime = (now - startedAt) % cycleDuration;
    const activeAnimation =
      cycleTime >= idleDuration && cycleTime < idleDuration + attackDuration
        ? attackAnimation
        : idleAnimation;
    const actionTime = activeAnimation === attackAnimation ? cycleTime - idleDuration : cycleTime;
    const frameCount = activeAnimation.end - activeAnimation.start + 1;
    const frame =
      activeAnimation.start +
      (Math.floor((actionTime / 1_000) * activeAnimation.frameRate) % frameCount);
    const column = frame % 8;
    const row = Math.floor(frame / 8);
    sprite.style.backgroundPosition = `${(column / 7) * 100}% ${(row / 5) * 100}%`;
    spotlightAnimationFrame = window.requestAnimationFrame(paint);
  };

  spotlightAnimationFrame = window.requestAnimationFrame(paint);
}

function renderPlayerSlots(): void {
  const local = currentLobby?.players.find((player) => player.id === lobbyPlayerId);
  const waitingForCrew = Boolean(
    local?.ready && currentLobby && !currentLobby.players.every((player) => player.ready)
  );
  const previewCharacter = CHARACTER_BY_ID.get(selectedCharacter) ?? CONTENT.characters[0];
  const slots = buildPlayerSlots({
    players: currentLobby?.players ?? [],
    localPlayerId: lobbyPlayerId,
    localWaiting: waitingForCrew,
    preview: {
      displayName: nameInput.value.trim(),
      characterId: selectedCharacter,
      characterName: previewCharacter?.displayName ?? selectedCharacter
    },
    characterName: (characterId) => CHARACTER_BY_ID.get(characterId)?.displayName ?? characterId
  });

  const grid = slotGrid(slots.length);
  playerSlots.style.setProperty("--slot-cols", String(grid.cols));
  playerSlots.style.setProperty("--slot-rows", String(grid.rows));
  playerSlots.dataset.count = String(slots.length);
  playerSlots.replaceChildren();
  for (const slot of slots) {
    const character = CHARACTER_BY_ID.get(slot.characterId);
    const item = document.createElement("li");
    item.className = `player-slot${slot.open ? " is-open" : " is-filled"}${slot.local ? " is-local" : ""}${slot.ready ? " is-ready" : ""}${slot.waiting ? " is-waiting" : ""}`;
    item.style.setProperty("--slot-color", slot.color);
    if (character) {
      item.style.setProperty("--fighter-primary", character.palette.primary);
      item.style.setProperty("--fighter-accent", character.palette.accent);
    }
    item.setAttribute("aria-label", slot.open ? `${slot.label} open` : `${slot.label} ${slot.displayName}`);

    const badge = document.createElement("span");
    badge.className = "slot-badge";
    badge.textContent = slot.label;

    const figure = document.createElement("span");
    figure.className = "slot-figure";
    if (slot.open) {
      const glove = document.createElement("span");
      glove.className = "slot-glove";
      glove.setAttribute("aria-hidden", "true");
      figure.append(glove);
    } else if (slot.local) {
      figure.dataset.localStage = "true";
    } else {
      figure.append(character ? idlePortrait(character) : createPortraitPlaceholder());
    }

    const info = document.createElement("span");
    info.className = "slot-info";
    const fighter = document.createElement("span");
    fighter.className = "slot-fighter";
    fighter.textContent = slot.open ? "" : slot.characterName;
    info.append(fighter);
    if (character && !slot.open) {
      const special = document.createElement("span");
      special.className = "slot-special";
      special.textContent = character.special.label;
      info.append(special);
    }

    const name = document.createElement("span");
    name.className = "slot-name";
    name.textContent = slot.displayName;

    const state = document.createElement("span");
    state.className = "slot-state";
    state.textContent = slot.open ? "Open" : slot.inMatch ? "In game" : slot.waiting ? "Waiting" : slot.ready ? "Ready" : slot.local ? "You" : "";
    state.hidden = state.textContent === "";

    item.append(badge, state, figure, info);
    if (character && !slot.open) item.append(fighterStats(character));
    item.append(name);
    playerSlots.append(item);
  }

  playerSlots.querySelector("[data-local-stage]")?.append(selectedFighterVisual);
}

function fighterStats(character: CharacterDefinition): HTMLElement {
  const list = document.createElement("span");
  list.className = "slot-stats";
  STAT_FIELDS.forEach(({ key, label }, index) => {
    const range = statRanges[index];
    const filled = statPips(character.stats[key], range?.min ?? 0, range?.max ?? 0);
    const row = document.createElement("span");
    row.className = "slot-stat";
    row.setAttribute("aria-label", `${label} ${filled} of ${STAT_PIPS}`);
    const name = document.createElement("span");
    name.className = "slot-stat-label";
    name.textContent = label;
    const pips = document.createElement("span");
    pips.className = "slot-stat-pips";
    for (let pip = 0; pip < STAT_PIPS; pip += 1) {
      const dot = document.createElement("i");
      if (pip < filled) dot.className = "is-on";
      pips.append(dot);
    }
    row.append(name, pips);
    list.append(row);
  });
  return list;
}

function idlePortrait(character: CharacterDefinition): HTMLElement {
  const sheet = character.spriteSheet;
  if (!sheet) return createPortraitPlaceholder();
  const sprite = document.createElement("span");
  sprite.className = "slot-portrait-sprite";
  sprite.dataset.fighter = character.id;
  sprite.style.backgroundImage = `url("${sheet.image}")`;
  const frame = sheet.animations.idle?.start ?? 0;
  const column = frame % 8;
  const row = Math.floor(frame / 8);
  sprite.style.backgroundPosition = `${(column / 7) * 100}% ${(row / 5) * 100}%`;
  return sprite;
}

bindMenuGamepad({
  menu,
  joinPanel,
  isBlocked: () => isOptionsMenuOpen() || isSoundtrackOpen()
});

nameInput.value = loadPlayerName();
renderPlayerSlots();
paintDifficulty(loadDifficulty(), false);
for (const button of difficultyButtons) {
  button.addEventListener("click", () => {
    if (difficultyLocked(currentLobby)) return;
    const tier = normalizeDifficultyTier(button.dataset.difficulty);
    saveDifficulty(tier);
    paintDifficulty(tier, false);
    if (
      joinPanel.classList.contains("lobby-open") &&
      currentLobby &&
      currentLobby.hostId === lobbyPlayerId
    ) {
      roomConnection?.setDifficulty(tier);
    }
  });
}

function loadDifficulty(): DifficultyTier {
  try {
    return normalizeDifficultyTier(localStorage.getItem(DIFFICULTY_STORAGE_KEY));
  } catch {
    return "medium";
  }
}

function saveDifficulty(tier: DifficultyTier): void {
  try {
    localStorage.setItem(DIFFICULTY_STORAGE_KEY, tier);
  } catch {
    // Private browsing can block storage. The in-memory selection still applies.
  }
}

function difficultyLocked(lobby: LobbyState | null): boolean {
  if (!joinPanel.classList.contains("lobby-open")) return false;
  if (!lobbyPlayerId || !lobby?.hostId) return true;
  return lobby.hostId !== lobbyPlayerId;
}

function paintDifficulty(tier: DifficultyTier, locked: boolean): void {
  for (const button of difficultyButtons) {
    const selected = button.dataset.difficulty === tier;
    button.classList.toggle("is-selected", selected);
    button.setAttribute("aria-pressed", String(selected));
    button.disabled = locked;
  }
  difficultyHostNote.hidden = !locked;
}

const invitedRoom = normalizeRoomCode(new URLSearchParams(location.search).get("room") ?? "");
const rememberedRoom = readPersistedRoomCode();
const autoJoinRoom = invitedRoom ?? rememberedRoom;
if (autoJoinRoom) {
  roomInput.value = autoJoinRoom;
  enterLobby(autoJoinRoom);
}

practiceButton.addEventListener("click", () => {
  if (roomConnection) leaveLobby();
  const [levelValue = "0", startType = "start"] = practiceStart.value.split(":");
  const startsAtBoss = startType === "boss" || startType === "boss2";
  startGame({
    mode: "practice",
    characterId: selectedCharacter,
    displayName: currentPlayerName() || undefined,
    difficulty: loadDifficulty(),
    startAt: {
      levelIndex: Number.parseInt(levelValue, 10) || 0,
      bossOnly: startsAtBoss,
      bossPhase: startType === "boss2" ? 2 : 1
    }
  });
});

createButton.addEventListener("click", () => {
  pushHostDifficulty = true;
  enterLobby(createRoomCode());
});

joinForm.addEventListener("submit", (event) => {
  event.preventDefault();
  pushHostDifficulty = false;
  const roomCode = normalizeRoomCode(roomInput.value);
  if (!roomCode) {
    status.textContent = "Enter the six-character code from your teammate’s invite.";
    roomInput.focus();
    return;
  }
  enterLobby(roomCode);
});

roomInput.addEventListener("input", () => {
  roomInput.value = roomInput.value
    .toUpperCase()
    .replace(/[^A-Z2-9]/g, "")
    .slice(0, 6);
});

nameInput.addEventListener("input", () => {
  const caret = nameInput.selectionStart ?? nameInput.value.length;
  const cleaned = nameInput.value
    .toUpperCase()
    .replace(/[^A-Z0-9 _-]/g, "")
    .slice(0, DISPLAY_NAME_MAX);
  nameInput.value = cleaned;
  nameInput.setSelectionRange(caret, caret);
  clearNameError();
  if (currentRoom) readyButton.disabled = true;
  savePlayerName(cleaned);
  renderPlayerSlots();
  scheduleRename(cleaned);
});

exitButton.addEventListener("click", exitGame);
optionsExitButton.addEventListener("click", () => {
  closeOptionsMenu();
  exitGame();
});
optionsCopyButton.addEventListener("click", () => copyInvite(optionsCopyButton, "Copy invite"));
fullscreenButton.addEventListener("click", () => {
  void toggleFullscreen();
});
optionsFullscreenButton.addEventListener("click", () => {
  void toggleFullscreen();
});
document.addEventListener("fullscreenchange", syncFullscreenButton);
window.addEventListener("resize", refreshGameScale);
leaveLobbyButton.addEventListener("click", () => leaveLobby("Left the room."));
readyButton.addEventListener("click", () => {
  const local = currentLobby?.players.find((player) => player.id === lobbyPlayerId);
  roomConnection?.setReady(!local?.ready);
});
copyButton.addEventListener("click", () => copyInvite(copyButton, "Copy invite"));
copyInviteButton.addEventListener("click", () => copyInvite(copyInviteButton, "Copy invite"));
if (!gameShell.requestFullscreen) fullscreenButton.hidden = true;
syncFullscreenButton();

async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await gameShell.requestFullscreen();
    }
  } catch {
    fullscreenButton.textContent = "Fullscreen unavailable";
  }
}

function syncFullscreenButton(): void {
  const fullscreen = document.fullscreenElement === gameShell;
  fullscreenButton.textContent = fullscreen ? "Exit fullscreen" : "Fullscreen";
  optionsFullscreenButton.textContent = fullscreen ? "Exit fullscreen" : "Fullscreen";
  fullscreenButton.setAttribute("aria-pressed", String(fullscreen));
  optionsFullscreenButton.setAttribute("aria-pressed", String(fullscreen));
  fullscreenButton.title = fullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen";
  optionsFullscreenButton.title = fullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen";
  refreshGameScale();
}

function refreshGameScale(): void {
  window.requestAnimationFrame(() => game?.scale.refresh());
}

function enterLobby(roomCode: string): void {
  closeRoomConnection();
  launchingMatch = false;
  lobbyPlayerId = null;
  currentLobby = null;
  persistRoomCode(roomCode);
  openLobbyChrome(roomCode);
  renderLobby({
    roomCode,
    players: [],
    difficulty: loadDifficulty(),
    hostId: ""
  });
  status.textContent = `Room ${roomCode} — waiting for teammates. Pick a fighter, then ready up.`;
  void preloadGameModules().catch(() => {
    // startGame reports a visible error and retries if the preload failed.
  });

  const connectionId = ++activeConnectionId;
  roomConnection = new RoomConnection(
    roomCode,
    selectedCharacter,
    {
      onWelcome: (playerId, phase, state, lobby) => {
        lobbyPlayerId = playerId;
        reconnectRoomAfterNameChange = null;
        clearNameError();
        readyButton.disabled = false;
        if (lobby) {
          renderLobby(lobby);
          if (pushHostDifficulty && lobby.hostId === playerId && phase === "lobby") {
            pushHostDifficulty = false;
            const saved = loadDifficulty();
            if (lobby.difficulty !== saved) roomConnection?.setDifficulty(saved);
          }
        }
        if (phase === "playing" && state) {
          void launchMatch(state);
        }
      },
      onLobby: (lobby, phase) => {
        renderLobby(lobby);
        if (phase === "lobby") showMenuKeepRoom();
      },
      onStart: (state) => {
        void launchMatch(state);
      },
      onSnapshot: () => undefined,
      onError: (message) => {
        if (
          message === DISPLAY_NAME_TAKEN_MESSAGE ||
          message.toLowerCase().includes("name is already taken")
        ) {
          if (!lobbyPlayerId) reconnectRoomAfterNameChange = currentRoom;
          showNameError(message);
          return;
        }
        status.textContent = message;
      },
      onClose: () => handleRoomClose(connectionId)
    },
    currentPlayerName() || undefined
  );
  roomConnection.connect();
}

function handleRoomClose(connectionId: number): void {
  if (connectionId !== activeConnectionId) return;
  if (reconnectRoomAfterNameChange) return;
  if (game || launchingMatch) showMenuKeepRoom();
  const roomCode = currentRoom;
  const leftOnPurpose = intentionalRoomLeave;
  intentionalRoomLeave = false;
  if (leftOnPurpose || !roomCode) {
    if (!roomCode) status.textContent = "Room connection closed.";
    else status.textContent = "Left the room.";
    resetLobbyChrome();
    clearRoomUrl();
    clearPersistedRoomCode();
    return;
  }
  status.textContent = `Room ${roomCode} — reconnecting…`;
  openLobbyChrome(roomCode);
  window.setTimeout(() => enterLobby(roomCode), 250);
}

function renderLobby(lobby: LobbyState): void {
  currentLobby = lobby;
  paintDifficulty(
    joinPanel.classList.contains("lobby-open") ? lobby.difficulty : loadDifficulty(),
    difficultyLocked(lobby)
  );
  const local = lobby.players.find((player) => player.id === lobbyPlayerId);
  if (local) {
    selectFighter(local.characterId, false);
    characterSelect.style.setProperty("--local-color", local.color);
    const requestedName = normalizeDisplayName(nameInput.value);
    if (!requestedName || requestedName === local.displayName) {
      clearNameError();
      readyButton.disabled = false;
    }
  }

  const waitingOn = lobby.players.filter(
    (player) => player.id !== lobbyPlayerId && !player.ready && !player.inMatch
  );
  const localReady = Boolean(local?.ready);
  const everyoneReady = lobby.players.length > 0 && lobby.players.every((player) => player.ready);
  const waitingForCrew = localReady && !everyoneReady;

  renderPlayerSlots();

  document.querySelectorAll<HTMLButtonElement>(".character-card").forEach((card) => {
    card.classList.toggle("selected", card.dataset.character === selectedCharacter);
  });
  paintFighterPicks();

  const readyCount = lobby.players.filter((player) => player.ready).length;
  readyButton.classList.toggle("is-ready", localReady);
  readyButton.textContent = localReady ? "Cancel ready" : "Ready up";
  paintReadyWait(
    waitingForCrew,
    everyoneReady,
    waitingOn.map((player) => player.displayName)
  );
  if (lobby.players.length === 0) {
    status.textContent = `Room ${lobby.roomCode} — connecting…`;
    return;
  }
  if (everyoneReady) {
    status.textContent = "Everyone's ready — clocking in!";
    return;
  }
  if (waitingForCrew) {
    status.textContent = waitingLine(waitingOn.map((player) => player.displayName));
    return;
  }
  status.textContent = `${readyCount}/${lobby.players.length} ready — hit Ready up when you're set.`;
}

function waitingLine(names: string[]): string {
  if (names.length === 0) return "You're ready. Waiting on the rest of the crew.";
  if (names.length === 1) return `You're ready. Waiting on ${names[0]}.`;
  if (names.length === 2) return `You're ready. Waiting on ${names[0]} and ${names[1]}.`;
  const last = names[names.length - 1];
  return `You're ready. Waiting on ${names.slice(0, -1).join(", ")}, and ${last}.`;
}

function paintReadyWait(waitingForCrew: boolean, everyoneReady: boolean, names: string[]): void {
  joinPanel.classList.toggle("is-waiting", waitingForCrew);
  const show = waitingForCrew || everyoneReady;
  readyWait.hidden = !show;
  readyWait.classList.toggle("is-go", everyoneReady && !waitingForCrew);
  if (!show) {
    if (joinPanel.classList.contains("lobby-open") && currentRoom) {
      joinStepLabel.textContent = "Room";
      joinHeading.textContent = currentRoom;
    } else {
      joinStepLabel.textContent = "Solo";
      joinHeading.textContent = "Battle";
    }
    lobbyHint.hidden = false;
    return;
  }
  lobbyHint.hidden = true;
  if (everyoneReady) {
    joinStepLabel.textContent = "Locked in";
    joinHeading.textContent = "Clocking in";
    readyWaitKicker.textContent = "Everyone's ready";
    readyWaitDetail.textContent = "Clocking in!";
    return;
  }
  joinStepLabel.textContent = "You're ready";
  joinHeading.textContent = "Waiting on the crew";
  readyWaitKicker.textContent = "You're ready";
  readyWaitDetail.textContent = waitingLine(names).replace(/^You're ready\. /, "");
}

async function launchMatch(state: WorldState): Promise<void> {
  if (launchingMatch || game) return;
  launchingMatch = true;
  const network = roomConnection;
  if (!network) {
    launchingMatch = false;
    return;
  }
  try {
    await startGame({
      mode: "online",
      characterId: selectedCharacter,
      roomCode: currentRoom ?? undefined,
      network,
      playerId: lobbyPlayerId ?? network.playerId ?? undefined,
      initialState: state,
      difficulty: state.difficultyTier ?? currentLobby?.difficulty ?? "medium"
    });
  } catch {
    roomConnection?.returnToLobby();
    showMenuKeepRoom();
    status.textContent = "The game could not start. Ready up to try again.";
  } finally {
    launchingMatch = false;
  }
}

function currentPlayerName(): string {
  return savePlayerName(nameInput.value);
}

function scheduleRename(value: string): void {
  window.clearTimeout(renameTimer);
  renameTimer = window.setTimeout(() => {
    if (reconnectRoomAfterNameChange) {
      const roomCode = reconnectRoomAfterNameChange;
      reconnectRoomAfterNameChange = null;
      enterLobby(roomCode);
      return;
    }
    roomConnection?.rename(normalizeDisplayName(value));
  }, 180);
}

function showNameError(message = DISPLAY_NAME_TAKEN_MESSAGE): void {
  nameError.textContent = message;
  nameError.hidden = false;
  nameInput.classList.add("invalid");
  nameInput.setAttribute("aria-invalid", "true");
  readyButton.disabled = true;
  status.textContent = message;
  nameInput.focus();
  nameInput.select();
}

function clearNameError(): void {
  nameError.hidden = true;
  nameError.textContent = "";
  nameInput.classList.remove("invalid");
  nameInput.removeAttribute("aria-invalid");
}

function openLobbyChrome(roomCode: string): void {
  currentRoom = roomCode;
  setRoomUrl(roomCode);
  joinPanel.classList.add("lobby-open");
  lobbyRoomCode.textContent = roomCode;
  joinStepLabel.textContent = "Room";
  joinHeading.textContent = roomCode;
  if (!lobbyPlayerId) readyButton.disabled = true;
}

function showMenuKeepRoom(): void {
  if (document.fullscreenElement === gameShell) void document.exitFullscreen();
  gameShell.hidden = true;
  gameOptionsDetails.hidden = true;
  menu.hidden = false;
  stopGameBgm();
  game?.destroy(true);
  registerLiveGame(null);
  game = null;
  launchingMatch = false;
  stopGameBgm();
  menuBgm.sync();
  if (currentRoom) openLobbyChrome(currentRoom);
}

function leaveLobby(message?: string): void {
  intentionalRoomLeave = true;
  clearPersistedRoomCode();
  closeRoomConnection();
  resetLobbyChrome();
  clearRoomUrl();
  if (message) status.textContent = message;
}

function resetLobbyChrome(): void {
  launchingMatch = false;
  lobbyPlayerId = null;
  currentLobby = null;
  currentRoom = null;
  reconnectRoomAfterNameChange = null;
  clearNameError();
  joinPanel.classList.remove("lobby-open", "is-waiting");
  joinStepLabel.textContent = "Solo";
  joinHeading.textContent = "Battle";
  lobbyHint.hidden = false;
  readyWait.hidden = true;
  readyWait.classList.remove("is-go");
  lobbyRoomCode.textContent = "";
  renderPlayerSlots();
  characterSelect.style.removeProperty("--local-color");
  readyButton.disabled = false;
  readyButton.classList.remove("is-ready");
  readyButton.textContent = "Ready up";
  paintDifficulty(loadDifficulty(), false);
  paintFighterPicks();
}

function closeRoomConnection(): void {
  const connection = roomConnection;
  roomConnection = null;
  connection?.close();
}

function setRoomUrl(roomCode: string): void {
  const url = new URL(location.href);
  url.searchParams.set("room", roomCode);
  history.replaceState({}, "", url);
}

function clearRoomUrl(): void {
  const url = new URL(location.href);
  url.searchParams.delete("room");
  history.replaceState({}, "", url);
}

async function copyInvite(button: HTMLButtonElement, idleLabel: string): Promise<void> {
  if (!currentRoom) return;
  const invite = new URL(location.href);
  invite.searchParams.set("room", currentRoom);
  try {
    await navigator.clipboard.writeText(invite.toString());
    button.textContent = "Invite copied";
    window.setTimeout(() => {
      button.textContent = idleLabel;
    }, 1_500);
  } catch {
    status.textContent = invite.toString();
  }
}

async function startGame(options: GameOptions): Promise<void> {
  if (options.mode === "online") {
    status.textContent = "Loading the fight…";
  }
  const { PhaserRuntime, GameScene } = await preloadGameModules();

  menuBgm.stop(true);
  stopGameBgm();
  game?.destroy(true);
  registerLiveGame(null);
  gameHost.replaceChildren();
  currentRoom = options.roomCode ?? currentRoom;
  menu.hidden = true;
  gameShell.hidden = false;
  const tier = options.difficulty ?? "medium";
  const place = currentRoom
    ? `Room ${currentRoom}`
    : options.startAt
      ? `Practice · Level ${options.startAt.levelIndex + 1}${
          options.startAt.bossPhase === 2
            ? " Boss · Second Form"
            : options.startAt.bossOnly
              ? " Boss"
              : ""
        }`
      : "Practice";
  roomLabel.textContent = `${place} · ${formatDifficultyTier(tier)}`;
  optionsRoomLabel.textContent = roomLabel.textContent;
  gameOptionsDetails.hidden = false;
  copyButton.hidden = !currentRoom;
  optionsCopyButton.hidden = !currentRoom;

  if (currentRoom) setRoomUrl(currentRoom);

  game = new PhaserRuntime.Game({
    type: PhaserRuntime.AUTO,
    parent: gameHost,
    width: 960,
    height: 540,
    backgroundColor: "#100921",
    pixelArt: true,
    antialias: false,
    roundPixels: true,
    scale: {
      mode: PhaserRuntime.Scale.FIT,
      autoCenter: PhaserRuntime.Scale.CENTER_BOTH
    },
    scene: new GameScene({ ...options, onResultsExit: () => exitGame() })
  });
  registerLiveGame(game);
}

async function loadGameModules() {
  const [{ default: PhaserRuntime }, { GameScene }] = await Promise.all([
    import("phaser"),
    import("./GameScene")
  ]);
  return { PhaserRuntime, GameScene };
}

function preloadGameModules(): ReturnType<typeof loadGameModules> {
  if (!gameModulesPromise) {
    gameModulesPromise = loadGameModules().catch((error: unknown) => {
      gameModulesPromise = null;
      throw error;
    });
  }
  return gameModulesPromise;
}

function exitGame(): void {
  const keepRoom = Boolean(currentRoom && roomConnection);
  if (keepRoom) roomConnection?.returnToLobby();
  showMenuKeepRoom();
  if (keepRoom && currentRoom) {
    persistRoomCode(currentRoom);
    status.textContent = `Room ${currentRoom} — still together. Ready up when you want another run.`;
    return;
  }
  resetLobbyChrome();
  clearRoomUrl();
}
